/**
 * One processing run: every file loose in the Finance Buddy folder is read by
 * Claude, written to the Sheet, then renamed and filed.
 *
 * Order per file is claim → extract → write rows → mark done → move, so a run
 * that dies part-way leaves either a file still in the drop zone (picked up
 * next time; its rows are replaced, not duplicated) or one already filed with
 * its Documents row complete. A file never leaves the drop zone before its
 * row says what it is.
 * See ARCHITECTURE.md, Pipeline.
 */
import type Anthropic from '@anthropic-ai/sdk'
import { catalog, type Catalog } from './categories'
import { EXTRACTION_MODEL, ExtractionError, MAX_FILE_BYTES, extract, inputKind, readBills, type DocumentInput } from './claude'
import { withBills } from './bills'
import { formatTimestamp } from './dates'
import { DUPLICATES, sameFile, sameStatement } from './duplicates'
import type { Extraction } from './extraction'
import { NEEDS_REVIEW, accountFor, canonicalize, fileName, folderPath, needsReview, type KnownAccount } from './filing'
import {
  DOCUMENT,
  FolderCache,
  RECOMMENDATIONS_DOC,
  SPREADSHEET,
  checksum,
  stillThere,
  download,
  fileInfo,
  exportAs,
  freeName,
  listFiles,
  moveAndRename,
  type DriveFile,
} from './google/drive'
import { exclusive, type Database, type Row } from './google/sheets'
import type { Meter } from './usage'

/** A `processing` claim younger than this belongs to another open tab. */
const CLAIM_MS = 10 * 60_000

export type FileStatus = 'uploading' | 'waiting' | 'reading' | 'filed' | 'review' | 'duplicate' | 'error' | 'skipped'

export interface FileProgress {
  id: string
  name: string
  status: FileStatus
  detail?: string
  link?: string
}

export interface RunContext {
  root: DriveFile
  db: Database
  anthropic: Anthropic
  /** Records each Claude call's tokens and cost in the Usage tab. */
  meter?: Meter
}


/** Files waiting in the drop zone, excluding the app's own Sheet. */
export async function scan(ctx: RunContext): Promise<DriveFile[]> {
  return appOwn(await listFiles(ctx.root.id), ctx.db.spreadsheetId)
}

/**
 * Leaves out the app's own files in the root folder: the Sheet, and the
 * Recommendations Doc. Reading the Doc as an upload filed it away, counted as
 * new data, and started another review that wrote another Doc.
 */
export function appOwn(files: DriveFile[], spreadsheetId: string): DriveFile[] {
  return files.filter((f) => f.id !== spreadsheetId && !(f.mimeType === DOCUMENT && f.name === RECOMMENDATIONS_DOC))
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function loadInput(file: DriveFile): Promise<DocumentInput> {
  if (file.mimeType === DOCUMENT) {
    return { kind: 'pdf', base64: await blobToBase64(await exportAs(file.id, 'application/pdf')) }
  }
  if (file.mimeType === SPREADSHEET) {
    return { kind: 'text', text: await (await exportAs(file.id, 'text/csv')).text() }
  }
  const kind = inputKind(file.mimeType)
  if (!kind) throw new ExtractionError(`Can't read ${file.mimeType} files yet`, 'review')
  if (Number(file.size ?? 0) > MAX_FILE_BYTES) {
    throw new ExtractionError('File is larger than Claude accepts in one request (about 23 MB)', 'review')
  }
  const blob = await download(file.id)
  if (kind === 'text') return { kind, text: await blob.text() }
  if (kind === 'pdf') return { kind, base64: await blobToBase64(blob) }
  return { kind, mediaType: file.mimeType as 'image/jpeg', base64: await blobToBase64(blob) }
}

export const autopayCell = (on: boolean | null) => (on === null ? null : on ? 'on' : 'off')

function rowsFor(file: DriveFile, x: Extraction, ids: (string | undefined)[]) {
  const accounts: Row[] = x.accounts.flatMap((a, i) =>
    ids[i] ? [{ account_id: ids[i], institution: x.institution, kind: a.kind, name: a.name, last4: a.last4 }] : [],
  )
  const source = { source_file_id: file.id }
  return {
    accounts,
    balances: x.balances.map(({ account_last4, autopay, ...b }) => ({
      account_id: accountFor(x, ids, account_last4),
      ...b,
      autopay: autopayCell(autopay),
      ...source,
    })),
    transactions: x.transactions.map(({ account_last4, ...t }, i) => ({
      txn_id: `${file.id}:${i + 1}`,
      account_id: accountFor(x, ids, account_last4),
      ...t,
      ...source,
    })),
    holdings: x.holdings.map(({ account_last4, ...h }) => ({ account_id: accountFor(x, ids, account_last4), ...h, ...source })),
    receipts: x.receipt
      ? [
          {
            file_id: file.id,
            date: x.receipt.date,
            merchant: x.receipt.merchant,
            total: x.receipt.total,
            tax: x.receipt.tax,
            items_json: JSON.stringify(x.receipt.items),
          },
        ]
      : [],
  }
}

/**
 * Claims this tab made for files it hasn't finished, by file id → claim time.
 * A run that dies part way (sign-in lapsed, the network dropped) leaves its
 * claims as `processing`; this tab may take its own back at once rather than
 * waiting out CLAIM_MS, while another tab's claims are still respected.
 */
const mine = new Map<string, number>()

/** What Claude read for a file this tab hasn't filed yet, so a retry after a failed Sheet write doesn't pay for the read again. */
const extracted = new Map<string, Extraction>()
const readKey = (file: DriveFile) => `${file.id}:${file.md5Checksum ?? ''}`

/** Runs under the pipeline lock, which every tab of this browser shares. */
async function claim(ctx: RunContext, file: DriveFile): Promise<boolean> {
  const docs = await ctx.db.read('Documents')
  const row = docs.rows.find((r) => r.file_id === file.id)
  // processed_at was the claim time before claimed_at existed.
  const claimed = Date.parse(String(row?.claimed_at || row?.processed_at))
  const ours = mine.get(file.id) === claimed
  if (row?.status === 'processing' && Date.now() - claimed < CLAIM_MS && !ours) return false
  const at = formatTimestamp(new Date())
  await ctx.db.upsert('Documents', 'file_id', [{ file_id: file.id, original_name: file.name, status: 'processing', claimed_at: at, md5: file.md5Checksum }])
  mine.set(file.id, Date.parse(at))
  return true
}

/**
 * A matched original that has been deleted from Drive is being replaced: its
 * rows come out of the Sheet and the new file is read normally. Returns
 * whether the original still counts.
 */
async function originalStands(ctx: RunContext, original: Row): Promise<boolean> {
  const id = String(original.file_id)
  if (await stillThere(id)) return true
  for (const tab of ['Balances', 'Transactions', 'Holdings'] as const) await ctx.db.replaceWhere(tab, 'source_file_id', id, [])
  await ctx.db.replaceWhere('Receipts', 'file_id', id, [])
  await ctx.db.upsert('Documents', 'file_id', [{ file_id: id, status: 'removed', review_reason: 'File deleted from Drive; its rows were removed' }])
  return false
}

/** Files a copy of something already read under Duplicates, writing no rows. */
async function setAside(ctx: RunContext, folders: FolderCache, file: DriveFile, original: Row, how: string): Promise<FileProgress> {
  const folderId = await folders.resolve([DUPLICATES])
  const moved = await moveAndRename(file, folderId, await freeName(folderId, file.name, file.id))
  const reason = `${how} ${String(original.name || original.original_name)}, already read`
  await ctx.db.upsert('Documents', 'file_id', [
    {
      file_id: file.id,
      name: moved.name,
      original_name: file.name,
      status: 'duplicate',
      duplicate_of: String(original.file_id),
      review_reason: reason,
      md5: file.md5Checksum,
      link: moved.webViewLink,
      processed_at: formatTimestamp(new Date()),
    },
  ])
  return { id: file.id, name: file.name, status: 'duplicate', detail: reason, link: moved.webViewLink }
}

/** Documents read by Claude at the same time. Sheet writes still happen one file at a time. */
const CONCURRENT_READS = 3

/** Shared by every tab of this browser, so two tabs never claim or file at once. */
const PIPELINE_LOCK = 'finance-buddy:pipeline'

/**
 * Runs a file's Sheet work one task at a time, here and in any other tab:
 * claiming a file, matching accounts and filing are each read-then-write, so
 * two at once would claim one file twice or file two documents against stale
 * Accounts. Only the Claude call, most of the time per file, runs in parallel.
 */
export function serial(): <T>(task: () => Promise<T>) => Promise<T> {
  let tail: Promise<unknown> = Promise.resolve()
  return <T>(task: () => Promise<T>) => {
    const run = () => exclusive(PIPELINE_LOCK, task)
    const next = tail.then(run, run)
    tail = next.catch(() => undefined)
    return next
  }
}
type Lock = ReturnType<typeof serial>

/** One file, start to finish. Forgets the file's claim and read only once its outcome is recorded in the Sheet. */
async function processFile(ctx: RunContext, folders: FolderCache, file: DriveFile, lock: Lock): Promise<FileProgress> {
  const out = await processOne(ctx, folders, file, lock)
  if (out.status !== 'skipped') {
    mine.delete(file.id)
    extracted.delete(readKey(file))
  }
  return out
}

/**
 * The Categories tab, read once per run (keyed by the run's lock) and only when
 * a file needs Claude: twenty files share one read. A failed read isn't kept.
 */
const runCategories = new WeakMap<Lock, Promise<Catalog>>()
function categoriesFor(ctx: RunContext, lock: Lock): Promise<Catalog> {
  let c = runCategories.get(lock)
  if (!c) {
    c = ctx.db.read('Categories').then((t) => catalog(t.rows))
    c.catch(() => runCategories.delete(lock))
    runCategories.set(lock, c)
  }
  return c
}

async function processOne(ctx: RunContext, folders: FolderCache, file: DriveFile, lock: Lock): Promise<FileProgress> {
  const before = await lock(async (): Promise<{ done: FileProgress } | { known: KnownAccount[] }> => {
    if (!(await claim(ctx, file))) {
      return { done: { id: file.id, name: file.name, status: 'skipped', detail: 'Being processed in another tab' } }
    }
    // An identical file is caught before spending a Claude call on it.
    const docs = (await ctx.db.read('Documents')).rows
    const copy = sameFile(docs, file.id, file.md5Checksum)
    if (copy && (await originalStands(ctx, copy))) return { done: await setAside(ctx, folders, file, copy, 'Identical to') }
    return { known: await knownAccounts(ctx) }
  })
  if ('done' in before) return before.done

  let raw: Extraction
  try {
    raw =
      extracted.get(readKey(file)) ??
      (await extract(
        ctx.anthropic,
        file.name,
        await loadInput(file),
        [...new Set(before.known.map((k) => k.institution).filter(Boolean))],
        ctx.meter,
        { categories: await categoriesFor(ctx, lock) },
      ))
    extracted.set(readKey(file), raw)
  } catch (error) {
    return lock(() => recordFailure(ctx, folders, file, error))
  }
  // Matched against Accounts as they are now, under the lock: a file read
  // alongside this one may have added the account since the read above.
  return lock(async () => {
    try {
      const { extraction: x, accountIds: ids } = canonicalize(raw, await knownAccounts(ctx))
      return await fileIt(ctx, folders, file, x, ids)
    } catch (error) {
      // fileIt fails only before the file moves, so it is still in the drop
      // zone: an error row (not a claim) lets the next run read it again.
      return recordFailure(ctx, folders, file, error)
    }
  })
}

async function knownAccounts(ctx: RunContext): Promise<KnownAccount[]> {
  return (await ctx.db.read('Accounts')).rows.map((r) => ({
    account_id: String(r.account_id ?? ''),
    institution: String(r.institution ?? ''),
    kind: String(r.kind ?? ''),
    name: String(r.name ?? ''),
    last4: String(r.last4 ?? ''),
  }))
}

/** Files a document Claude could not read in Needs Review, or records the error for a retry. Runs under the lock. */
async function recordFailure(ctx: RunContext, folders: FolderCache, file: DriveFile, error: unknown): Promise<FileProgress> {
  const message = (error as Error).message
  if (error instanceof ExtractionError && error.disposition === 'review') {
    const moved = await moveAndRename(file, await folders.resolve([NEEDS_REVIEW]), file.name)
    await ctx.db.upsert('Documents', 'file_id', [
      { file_id: file.id, name: file.name, status: 'review', review_reason: message, link: moved.webViewLink, processed_at: formatTimestamp(new Date()) },
    ])
    return { id: file.id, name: file.name, status: 'review', detail: message, link: moved.webViewLink }
  }
  await ctx.db.upsert('Documents', 'file_id', [{ file_id: file.id, status: 'error', error: message, processed_at: formatTimestamp(new Date()) }])
  return { id: file.id, name: file.name, status: 'error', detail: message }
}

/**
 * Moves a filed document out of the drop zone. If the request fails, Drive is
 * asked where the file is: a move whose reply was lost has still happened, and
 * calling it an error would hide a statement whose rows are all written.
 */
async function moveOrConfirm(file: DriveFile, folderId: string, name: string): Promise<DriveFile> {
  try {
    return await moveAndRename(file, folderId, name)
  } catch (error) {
    const now = await fileInfo(file.id).catch(() => undefined)
    if (now?.parents?.includes(folderId)) return now
    throw error
  }
}

/** Writes one read document's rows, then files it. Runs under the lock. */
async function fileIt(ctx: RunContext, folders: FolderCache, file: DriveFile, x: Extraction, ids: (string | undefined)[]): Promise<FileProgress> {
  // Re-read here, not before the Claude call: another file in this batch may
  // have been the same statement and been filed while this one was read.
  const docs = (await ctx.db.read('Documents')).rows
  const copy = sameFile(docs, file.id, file.md5Checksum)
  if (copy && (await originalStands(ctx, copy))) return setAside(ctx, folders, file, copy, 'Identical to')
  const same = sameStatement(docs, file.id, x, ids)
  if (same && (await originalStands(ctx, same))) return setAside(ctx, folders, file, same, 'Same statement as')

  const rows = rowsFor(file, x, ids)
  if (rows.accounts.length) await ctx.db.upsert('Accounts', 'account_id', rows.accounts, true)
  await ctx.db.replaceWhere('Balances', 'source_file_id', file.id, rows.balances)
  await ctx.db.replaceWhere('Transactions', 'source_file_id', file.id, rows.transactions)
  await ctx.db.replaceWhere('Holdings', 'source_file_id', file.id, rows.holdings)
  await ctx.db.replaceWhere('Receipts', 'file_id', file.id, rows.receipts)

  const folderId = await folders.resolve(folderPath(x))
  const name = await freeName(folderId, fileName(x, file.name), file.id)
  const review = needsReview(x)
  // The row is complete before the file leaves the drop zone: a failure after
  // the move would otherwise leave a filed document no run looks at again.
  await ctx.db.upsert('Documents', 'file_id', [
    {
      file_id: file.id,
      name,
      original_name: file.name,
      doc_type: x.doc_type,
      institution: x.institution,
      account_id: ids.filter(Boolean).join(', '),
      period_start: x.period_start,
      period_end: x.document_date,
      status: review ? 'review' : 'done',
      confidence: x.confidence,
      review_reason: x.review_reason,
      summary: x.summary,
      error: '',
      model: EXTRACTION_MODEL,
      processed_at: formatTimestamp(new Date()),
      md5: file.md5Checksum,
    },
  ])
  const moved = await moveOrConfirm(file, folderId, name)
  try {
    await ctx.db.upsert('Documents', 'file_id', [{ file_id: file.id, link: moved.webViewLink }])
  } catch {
    // The document is filed and counted; only the link in the Sheet is missing.
  }
  return {
    id: file.id,
    name,
    status: review ? 'review' : 'filed',
    detail: review ? x.review_reason || 'Low confidence' : `${folderPath(x).join(' / ')}`,
    link: moved.webViewLink,
  }
}

/**
 * Documents read before checksums were recorded get theirs from Drive, so an
 * identical copy of one is recognised without a Claude call. Runs only when
 * there is something to process, and once per document.
 */
async function backfillChecksums(ctx: RunContext): Promise<void> {
  const docs = await ctx.db.read('Documents')
  const missing = docs.rows.filter((d) => !d.md5 && (d.status === 'done' || d.status === 'review'))
  if (!missing.length) return
  const found: Row[] = []
  for (const d of missing) {
    try {
      const md5 = await checksum(String(d.file_id))
      if (md5) found.push({ file_id: d.file_id, md5 })
    } catch {
      // Deleted or no longer visible: leave it; the same-statement check still applies.
    }
  }
  // Only the md5 cells, into the tab as it is now: the Drive calls take
  // seconds, and another tab may have written Documents meanwhile.
  if (found.length) await ctx.db.upsert('Documents', 'file_id', found, true)
}

/** Processes `files`, reading up to CONCURRENT_READS at once, reporting each as it changes. */
export async function run(ctx: RunContext, files: DriveFile[], onProgress: (p: FileProgress) => void): Promise<FileProgress[]> {
  const folders = new FolderCache(ctx.root.id)
  const results: FileProgress[] = new Array(files.length)
  if (files.length) await backfillChecksums(ctx)
  const lock = serial()
  let next = 0
  const worker = async () => {
    while (next < files.length) {
      const i = next++
      const file = files[i]
      onProgress({ id: file.id, name: file.name, status: 'reading' })
      try {
        results[i] = await processFile(ctx, folders, file, lock)
      } catch (error) {
        results[i] = { id: file.id, name: file.name, status: 'error', detail: (error as Error).message }
      }
      onProgress(results[i])
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENT_READS, files.length) }, worker))
  return results
}

/**
 * Re-reads the due date, minimum and AutoPay from statements read before
 * those fields were captured, and writes them into each statement's balance
 * rows. Nothing else about the statement changes. Returns the files that
 * could not be read, with why.
 */
export async function readDueDates(ctx: RunContext, fileIds: string[], onDone?: (count: number) => void): Promise<{ id: string; error: string }[]> {
  const failed: { id: string; error: string }[] = []
  let done = 0
  for (const id of fileIds) {
    try {
      const file = await fileInfo(id)
      const { bills } = await readBills(ctx.anthropic, file.name, await loadInput(file), ctx.meter)
      await exclusive(PIPELINE_LOCK, async () => {
        const [balances, accounts] = await Promise.all([ctx.db.read('Balances'), ctx.db.read('Accounts')])
        const last4 = new Map(accounts.rows.map((a) => [String(a.account_id ?? ''), String(a.last4 ?? '')]))
        const rows = balances.rows.filter((r) => r.source_file_id === id)
        await ctx.db.replaceWhere('Balances', 'source_file_id', id, withBills(rows, bills, (a) => last4.get(a) ?? ''))
      })
    } catch (error) {
      failed.push({ id, error: (error as Error).message })
    }
    onDone?.(++done)
  }
  return failed
}
